import { ReportDataWrapper } from '#components/ReportDataWrapper'
import { StatCard } from '#components/StatCard'
import { useFetch } from '#hooks/useAdminResource'
import { formatPercentage } from '#utils/format'

interface QueueCounts {
  active: number
  completed: number
  delayed: number
  failed: number
  prioritized: number
  waiting: number
  'waiting-children': number
}

const statusConfig: {
  key: keyof QueueCounts
  label: string
  color: string
}[] = [
  { key: 'active', label: 'Active', color: 'text-info' },
  { key: 'waiting', label: 'Waiting', color: 'text-warning' },
  { key: 'delayed', label: 'Delayed', color: 'text-primary' },
  { key: 'completed', label: 'Completed', color: 'text-success' },
  { key: 'failed', label: 'Failed', color: 'text-error' },
]

interface QueueStatusProps {
  /** Admin API endpoint returning BullMQ job counts (e.g. `trackings/queue-status`). */
  endpoint: string
}

const QueueStatus = ({ endpoint }: QueueStatusProps) => {
  const {
    data: counts,
    isLoading,
    error,
    refetch: refresh,
  } = useFetch<QueueCounts>(endpoint, {})

  const total = counts
    ? statusConfig.reduce((sum, { key }) => sum + (counts[key] || 0), 0)
    : 0

  return (
    <ReportDataWrapper
      isLoading={isLoading}
      error={error}
      loadingMessage="Loading queue status..."
      onRetry={() => refresh()}
    >
      {counts && (
        <div className="space-y-6">
          <div className="flex justify-end">
            <button
              type="button"
              className="btn btn-sm btn-outline"
              onClick={() => refresh()}
            >
              Refresh
            </button>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {statusConfig.map(({ key, label, color }) => (
              <StatCard
                key={key}
                title={label}
                value={(counts[key] || 0).toLocaleString()}
                color={color}
              />
            ))}
          </div>

          <div className="stats shadow w-full">
            <div className="stat">
              <div className="stat-title">Total Jobs</div>
              <div className="stat-value">{total.toLocaleString()}</div>
            </div>
          </div>

          {total > 0 && (
            <div className="card bg-base-100 shadow-xl">
              <div className="card-body">
                <h2 className="card-title">Distribution</h2>
                <div className="overflow-x-auto">
                  <table className="table table-zebra">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th className="text-right">Count</th>
                        <th className="text-right">Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statusConfig
                        .filter(({ key }) => (counts[key] || 0) > 0)
                        .map(({ key, label, color }) => (
                          <tr key={key}>
                            <td className={`font-medium ${color}`}>{label}</td>
                            <td className="text-right">
                              {(counts[key] || 0).toLocaleString()}
                            </td>
                            <td className="text-right">
                              {formatPercentage(counts[key] || 0, total)}%
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </ReportDataWrapper>
  )
}

export { QueueStatus }
